'use client';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { Workflows } from '@/components/workflows';
import { Urls } from '@/lib/urls';
import { Pencil } from 'lucide-react';
import { PlusIcon } from '@heroicons/react/16/solid';
import { useRouter } from 'next/navigation';
import { Skeleton } from '@/components/ui/skeleton';
import { useEffect, useMemo, useState } from 'react';
import {
  useWorkflows,
  type WorkflowSettings,
} from '@/providers/workflow-provider';
import { AnimatedIndicator } from '@/components/ui/animated-indicator';
import { EmptyWorkflows } from '@/components/workflows/empty-workflows';
import { WorkflowPresetsModal } from '@/components/workflows/presets/workflow-presets-modal';
import { cn } from '@/lib/cn';

type WorkflowsWidgetProps = {
  isWorkEngagementDashboard: boolean;
  dashboardWorkEngagementId?: string;
};

export function WorkflowsWidget({
  isWorkEngagementDashboard,
  dashboardWorkEngagementId,
}: WorkflowsWidgetProps) {
  const router = useRouter();
  const {
    sharedFolders,
    userWorkflows,
    workEngagementWorkflows,
    isLoadingUserWorkflows,
    isLoadingWorkEngagementWorkflows,
    workflowSettings,
    setWorkflowSettings,
  } = useWorkflows();

  const [isWorkflowPresetsModalOpen, setIsWorkflowPresetsModalOpen] =
    useState(false);

  const visibleWorkEngagementWorkflows = useMemo(() => {
    if (!workEngagementWorkflows?.length) return workEngagementWorkflows;
    if (!isWorkEngagementDashboard) return [];
    if (
      isWorkEngagementDashboard &&
      dashboardWorkEngagementId !== undefined &&
      dashboardWorkEngagementId !== ''
    ) {
      return workEngagementWorkflows.filter(
        (wf) => wf.work_engagement_id === dashboardWorkEngagementId
      );
    }
    return workEngagementWorkflows;
  }, [
    workEngagementWorkflows,
    isWorkEngagementDashboard,
    dashboardWorkEngagementId,
  ]);

  useEffect(() => {
    if (isLoadingUserWorkflows || isLoadingWorkEngagementWorkflows) return;
    if (!userWorkflows || !visibleWorkEngagementWorkflows) return;

    const dashboardIds = workflowSettings.expandedItems.dashboard;

    // Om dashboard-key inte finns ännu
    if (!dashboardIds) {
      const allFolders = [
        ...(userWorkflows?.folders ?? []),
        ...(visibleWorkEngagementWorkflows?.flatMap((wf) => wf.folders ?? []) ??
          []),
      ];

      if (allFolders.length > 0) {
        const pinnedDashboardIds = allFolders.map((folder) => folder.client_id);

        const newSettings: WorkflowSettings = {
          ...workflowSettings,
          expandedItems: {
            ...workflowSettings.expandedItems,
            dashboard: pinnedDashboardIds,
          },
        };

        setWorkflowSettings(newSettings);
      }
    }
  }, [
    isLoadingUserWorkflows,
    isLoadingWorkEngagementWorkflows,
    userWorkflows,
    visibleWorkEngagementWorkflows,
    workflowSettings,
    setWorkflowSettings,
  ]);

  const isFetching = isLoadingUserWorkflows || isLoadingWorkEngagementWorkflows;
  const hasSharedFolders = sharedFolders && sharedFolders.length > 0;

  const isEmptyWorkflows = useMemo(() => {
    const checkUser = userWorkflows?.folders?.some((folder) => folder.pinned);
    const checkWorkEngagement = visibleWorkEngagementWorkflows?.some(
      (workEngagement) =>
        workEngagement.folders?.some((folder) => folder.pinned)
    );
    return !checkUser && !checkWorkEngagement;
  }, [userWorkflows, visibleWorkEngagementWorkflows]);

  return (
    <>
      {isWorkflowPresetsModalOpen && (
        <WorkflowPresetsModal onOpenChange={setIsWorkflowPresetsModalOpen} />
      )}

      <Card className='h-full gap-0 p-0'>
        <CardHeader className='flex flex-row items-center justify-between gap-2 border-b-0 px-4 pt-4 text-sm font-medium'>
          <CardTitle className='flex items-center gap-2'>
            Arbetsflöden
          </CardTitle>
          <div className='flex items-center gap-2'>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  className='relative'
                  variant={'outline'}
                  size={'icon'}
                  onClick={() => setIsWorkflowPresetsModalOpen(true)}
                >
                  <PlusIcon className='size-4' />
                </Button>
              </TooltipTrigger>
              <TooltipContent className='font-normal'>
                Lägg till från Pigello arbetsflöden
              </TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  className='relative'
                  variant={'outline'}
                  size={'icon'}
                  onClick={() => router.push(Urls.dashboard.workflows)}
                >
                  {hasSharedFolders && <AnimatedIndicator />}
                  <Pencil className='size-4' />
                </Button>
              </TooltipTrigger>
              <TooltipContent className='font-normal'>Redigera</TooltipContent>
            </Tooltip>
          </div>
        </CardHeader>
        <CardContent
          className={cn('relative px-4 pb-4', {
            'pb-1': !isFetching,
          })}
        >
          {isFetching ? (
            <div className='flex flex-col gap-4'>
              {Array.from({ length: 2 }, (_, i) => (
                <Skeleton key={i} className='h-12 w-full' />
              ))}
            </div>
          ) : isEmptyWorkflows ? (
            <EmptyWorkflows />
          ) : (
            <>
              {visibleWorkEngagementWorkflows?.map((workEngagementWorkflow) => (
                <Workflows
                  key={workEngagementWorkflow.work_engagement_id}
                  workEngagementId={workEngagementWorkflow.work_engagement_id}
                  isDashboard
                />
              ))}
              <Workflows isDashboard />
            </>
          )}
        </CardContent>
      </Card>
    </>
  );
}
